//C:\Users\Anish\xpr_survey\XPRSuvey-App\src\Components\Analytics\Pages\BuildAnalytics.jsx
import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import {
  BarChart3,
  ClipboardList,
  Layers,
  ChevronDown,
  ListChecks,
  SlidersHorizontal,
  ShieldAlert,
  Flag,
  Plus,
  Trash2,
  Info,
  Ban,
  LayoutGrid,
} from "lucide-react";
import {
  getSurveyData,
  getSurveyQuestions,
} from "../../Survey/Services/SurveyService";
import AddAssessmentFactor from "../Modal/AddAssessmentFactor";
import {
  getAssessmentFactor,
  saveQuestionAnalytics,
  getQuestionAnalytics,
  getScoringConfig,
  saveScoringConfig,
} from "../Services/BuildAnalytics";
import Select from "react-select";
import { motion, AnimatePresence } from "framer-motion";
import SurveyNavTabs from "../../../Utility/SurveyNavTabs";
import { sanitizeHtml, DESCRIPTION_RICH_CONTENT_STYLES } from "../../../Utility/sanitizeHtml";

const DEFAULT_SECTIONS = ["Section A", "Section B"];
const OPTION_BASED_TYPES = ["Multiple Choice", "Check Boxes", "Dropdown", "Rank"];
const SCALE_BASED_TYPES = ["Rating Bar", "Answer in Number"];

const getScoreShape = (optionType) => {
  if (OPTION_BASED_TYPES.includes(optionType)) return "options";
  if (SCALE_BASED_TYPES.includes(optionType)) return "scale";
  return "none";
};

const makeId = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 7);

const CLASSIFICATION_OPTIONS = [
  { id: "Developmental", label: "Developmental", accent: "#d97706" },
  { id: "RedFlag", label: "Red flag", accent: "#dc2626" },
];

const DEFAULT_FACTOR1_BANDS = [
  { id: "above", label: "Above Average", min: 15, max: 21 },
  { id: "average", label: "Average", min: 8, max: 14 },
  { id: "below", label: "Below Average", min: 0, max: 7 },
];

const DEFAULT_FACTOR2_BANDS = [
  { id: "below", label: "Below Average", min: 0, max: 9 },
  { id: "average", label: "Average", min: 10, max: 17 },
  { id: "above", label: "Above Average", min: 18, max: 26 },
];

const DEFAULT_GRID = {
  above: {
    below: {
      grade: "C+",
      title: "Caution",
      description: "Fits the branch but converts poorly. Weak hire; coach conversion.",
    },
    average: {
      grade: "B+",
      title: "Hire",
      description: "Will fit; coach conversion skill.",
    },
  },
  average: {
    below: {
      grade: "C",
      title: "Low chance",
      description: "Average fit, weak conversion. Only if interview is exceptional.",
    },
    average: {
      grade: "B",
      title: "Conditional Hire",
      description: "Proceed with a structured onboarding plan.",
    },
  },
  below: {
    below: {
      grade: "D",
      title: "Do not proceed",
      description: "Weak fit + weak conversion.",
    },
    average: {
      grade: "C",
      title: "High risk",
      description: "Converts but unlikely to stay/fit. Strong interview needed.",
    },
  },
};
DEFAULT_GRID.above.above = {
  grade: "A",
  title: "Strong Hire",
  description: "Fits the branch & converts. Proceed.",
};
DEFAULT_GRID.average.above = {
  grade: "B+",
  title: "Hire",
  description: "Converts well; support branch / matrix fit.",
};
DEFAULT_GRID.below.above = {
  grade: "C+",
  title: "Short-tenure risk",
  description: "May convert early, then exit. Not a safe hire.",
};

const DEFAULT_SECTION_B_BANDS = [
  { id: "strong", label: "Strongly Recommended", min: 40, max: 50 },
  { id: "recommended", label: "Recommended", min: 32, max: 39 },
  { id: "development", label: "Recommended with Development Plan", min: 24, max: 31 },
  { id: "caution", label: "Caution", min: 16, max: 23 },
  { id: "not_recommended", label: "Not Recommended", min: 0, max: 15 },
];

const DEFAULT_RED_FLAG_REVIEW_THRESHOLD = 2;

// Shared styles for single Select components
const singleSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: 40,
    borderRadius: 8,
    borderColor: state.isFocused ? "#7f35b2" : "#d1d5db",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(59,178,144,.20)" : "none",
    "&:hover": { borderColor: "#7f35b2" },
  }),
  menu: (provided) => ({ ...provided, zIndex: 9999 }),
  menuList: (provided) => ({ ...provided, padding: 0 }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? "#7f35b2" : state.isFocused ? "#c2a8f026" : "#fff",
    color: state.isSelected ? "#fff" : "#111827",
    cursor: "pointer",
    ":active": { backgroundColor: "#c2a8f066" },
  }),
  placeholder: (provided) => ({ ...provided, color: "#6B7280", fontWeight: 500 }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    color: state.isFocused ? "#7f35b2" : "#6B7280",
    ":hover": { color: "#7f35b2" },
  }),
  clearIndicator: (provided) => ({ ...provided, color: "#7f35b2", ":hover": { color: "#48086f" } }),
  indicatorSeparator: () => ({ display: "none" }),
  input: (provided) => ({ ...provided, color: "#111827" }),
  singleValue: (provided) => ({ ...provided, color: "#111827" }),
};

const ClassificationPicker = ({ value, onChange, disabled }) => (
  <div className="flex flex-wrap gap-1.5">
    {CLASSIFICATION_OPTIONS.map((option) => {
      const active = value === option.id;
      return (
        <button
          key={option.id}
          type="button"
          onClick={() => onChange(option.id)}
          disabled={disabled}
          aria-pressed={active}
          className={`rounded-full border px-2.5 py-1 text-xs font-medium transition ${disabled ? "cursor-default opacity-70" : ""}`}
          style={
            active
              ? { backgroundColor: option.accent, borderColor: option.accent, color: "#fff" }
              : { backgroundColor: "#fff", borderColor: "#e5e7eb", color: "#4b5563" }
          }
        >
          {option.id === "RedFlag" && <Flag size={11} className="mr-1 inline -mt-0.5" />}
          {option.label}
        </button>
      );
    })}
  </div>
);

const BandRangeInputs = ({ band, onChange, disabled }) => (
  <div className="flex items-center gap-1.5">
    <input
      type="number"
      value={band.min}
      disabled={disabled}
      onChange={(e) => onChange("min", e.target.value)}
      className="w-14 rounded-md border border-gray-200 px-2 py-1 text-xs focus:border-[#7f35b2]"
    />
    <span className="text-xs text-gray-400">to</span>
    <input
      type="number"
      value={band.max}
      disabled={disabled}
      onChange={(e) => onChange("max", e.target.value)}
      className="w-14 rounded-md border border-gray-200 px-2 py-1 text-xs focus:border-[#7f35b2]"
    />
  </div>
);

const GridBandRangeInputs = ({ band, onChange, disabled }) => (
  <div className="flex items-center gap-2">
    <input
      type="number"
      value={band.min}
      disabled={disabled}
      onChange={(e) => onChange("min", e.target.value)}
      className="w-14 h-8 rounded-lg border border-gray-200 bg-white text-center text-xs font-semibold text-gray-700 focus:border-[#7f35b2] focus:ring-1 focus:ring-[#7f35b2]/20"
    />
    <span className="text-gray-400 font-medium">-</span>
    <input
      type="number"
      value={band.max}
      disabled={disabled}
      onChange={(e) => onChange("max", e.target.value)}
      className="w-14 h-8 rounded-lg border border-gray-200 bg-white text-center text-xs font-semibold text-gray-700 focus:border-[#7f35b2] focus:ring-1 focus:ring-[#7f35b2]/20"
    />
  </div>
);

const stripHtml = (html) => {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  return (doc.body.textContent || "").trim();
};

const BuildAnalytics = () => {
  const { surveyUID } = useParams();
  const location = useLocation();

  const [questions, setQuestions] = useState([]);
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(0);
  const [showFactorDrawer, setShowFactorDrawer] = useState(false);
  const [assessmentFactor, setAssessmentFactor] = useState([]);
  const [selectedSection, setSelectedSection] = useState(DEFAULT_SECTIONS[0]);
  const [expandedSection, setExpandedSection] = useState(DEFAULT_SECTIONS[0]);
  const [viewMode, setViewMode] = useState("questions");

  // Dynamic Factor Variables
  const [factor1ID, setFactor1ID] = useState(null); // Y-Axis
  const [factor2ID, setFactor2ID] = useState(null); // X-Axis
  const [factor1Bands, setFactor1Bands] = useState(DEFAULT_FACTOR1_BANDS);
  const [factor2Bands, setFactor2Bands] = useState(DEFAULT_FACTOR2_BANDS);

  const [grid, setGrid] = useState(DEFAULT_GRID);
  const [sectionBBands, setSectionBBands] = useState(DEFAULT_SECTION_B_BANDS);
  const [redFlagReviewThreshold, setRedFlagReviewThreshold] = useState(DEFAULT_RED_FLAG_REVIEW_THRESHOLD);
  const [savingConfig, setSavingConfig] = useState(false);
  const [usingDefaultConfig, setUsingDefaultConfig] = useState(true);

  // const { surveyName, surveyDescription, viewOnly = false } = location.state || {};
  const { surveyName, surveyDescription, viewOnly = false, unlockAnalytics = false } = location.state || {};
  const analyticsViewOnly = viewOnly && !unlockAnalytics;


  useEffect(() => {
    if (surveyUID) {
      loadSurvey();
      loadAssessmentFactors();
      loadScoringConfig();
    }
  }, [surveyUID]);

  const loadSurvey = async () => {
    try {
      setLoading(true);

      const request = { surveyUID, surveyName, surveyDescription };
      const surveyResponse = await getSurveyData(request);
      if (surveyResponse.data.status.toLowerCase() === "success") {
        setSurvey(surveyResponse.data.surveyDetails[0]);
      }

      const questionResponse = await getSurveyQuestions(request);
      if (questionResponse.data.status.toLowerCase() === "success") {
        const mapped = questionResponse.data.surveyDetails.map((q) => {
          const scoreShape = getScoreShape(q.optionType);

          return {
            questionUID: q.uniqueKey,
            questionName: q.questionName,
            optionType: q.optionType,
            scoreShape,
            section: q.section || DEFAULT_SECTIONS[0],
            channel: q.channel || "",
            experienceType: q.experienceType || "",

            factor: [],
            isAnalyticsEnabled: false,
            options: (q.options || []).map((o) => ({
              optionID: o.optionID,
              optionName: o.optionName,
              scores: {},
              isGatekeeper: false,
              score: 0,
              classification: null,
            })),
            scoreBands: scoreShape === "scale" ? [] : undefined,
          };
        });

        setQuestions(mapped);
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const loadAssessmentFactors = async () => {
    try {
      const response = await getAssessmentFactor(surveyUID);
      if (response.data.status && response.data.status.toLowerCase() === "success") {
        setAssessmentFactor(response.data.assessmentFactors || []);
      }
    } catch (err) {
    }
  };

  const loadScoringConfig = async () => {
    try {
      const response = await getScoringConfig(surveyUID);
      if (response.data.status && response.data.status.toLowerCase() === "success" && response.data.config) {
        const config = response.data.config;

        // Load dynamically mapped factors 
        setFactor1ID(config.factor1ID || null);
        setFactor2ID(config.factor2ID || null);
        setFactor1Bands(config.factor1Bands || config.survivalBands || DEFAULT_FACTOR1_BANDS);
        setFactor2Bands(config.factor2Bands || config.productivityBands || DEFAULT_FACTOR2_BANDS);

        setGrid(config.grid || DEFAULT_GRID);
        setSectionBBands(
          (config.sectionBBands || DEFAULT_SECTION_B_BANDS).map((band) => ({
            ...band,
            id: band.id || makeId(),
          }))
        );
        setRedFlagReviewThreshold(config.redFlagReviewThreshold ?? DEFAULT_RED_FLAG_REVIEW_THRESHOLD);
        setUsingDefaultConfig(false);
      }
    } catch (err) {
    }
  };

  const handleSaveScoringConfig = async () => {
    try {
      setSavingConfig(true);
      const payload = {
        surveyUID,
        config: {
          factor1ID,
          factor2ID,
          factor1Bands: factor1Bands.map(b => ({ ...b, min: Number(b.min), max: Number(b.max) })),
          factor2Bands: factor2Bands.map(b => ({ ...b, min: Number(b.min), max: Number(b.max) })),
          grid,
          sectionBBands: sectionBBands.map(b => ({ ...b, min: Number(b.min), max: Number(b.max) })),
          redFlagReviewThreshold
        },
      };
      const response = await saveScoringConfig(payload);
      if (response.data.status?.toLowerCase() === "success") {
        CustomToast("success", "Scoring rules saved successfully.");
        setUsingDefaultConfig(false);
      } else {
        CustomToast("fail", response.data.message || "Failed to save scoring rules.");
      }
    } catch (error) {
      CustomToast("fail", "Couldn't reach the scoring-config endpoint. Changes are only kept for this session.");
    } finally {
      setSavingConfig(false);
    }
  };

  const updateFactorSelection = (index, factorIds) => {
    const copy = [...questions];
    const question = copy[index];
    question.factor = factorIds;
    question.options = question.options.map((option) => {
      const existingKeys = Object.keys(option.scores || {});
      const sharedScore = existingKeys.length > 0 ? option.scores[existingKeys[0]] : 0;
      const nextScores = {};
      factorIds.forEach((factorId) => { nextScores[factorId] = sharedScore; });
      return { ...option, scores: nextScores };
    });
    setQuestions(copy);
  };

  const updateOptionScoreForAllFactors = (qIndex, optionIndex, value) => {
    const copy = [...questions];
    const question = copy[qIndex];
    const option = question.options[optionIndex];
    const nextScores = {};
    question.factor.forEach((factorId) => { nextScores[factorId] = value; });
    option.scores = nextScores;
    setQuestions(copy);
  };

  const updateGatekeeper = (qIndex, optionIndex, checked) => {
    const copy = [...questions];
    copy[qIndex].options[optionIndex].isGatekeeper = checked;
    setQuestions(copy);
  };

  const updateOptionScoreB = (qIndex, optionIndex, value) => {
    const copy = [...questions];
    copy[qIndex].options[optionIndex].score = value;
    setQuestions(copy);
  };

  const updateClassification = (qIndex, optionIndex, classification) => {
    const copy = [...questions];
    const current = copy[qIndex].options[optionIndex].classification;
    copy[qIndex].options[optionIndex].classification = current === classification ? null : classification;
    setQuestions(copy);
  };

  // ---- Scale-type (Rating Bar / Answer in Number) score-band handlers ----
  const addScoreBand = (qIndex) => {
    setQuestions((prev) => {
      const copy = [...prev];
      const q = copy[qIndex];
      copy[qIndex] = {
        ...q,
        scoreBands: [...(q.scoreBands || []), { id: makeId(), label: "New band", min: 0, max: 0, score: 0 }],
      };
      return copy;
    });
  };

  const updateScoreBand = (qIndex, bandId, field, value) => {
    setQuestions((prev) => {
      const copy = [...prev];
      const q = copy[qIndex];
      copy[qIndex] = {
        ...q,
        scoreBands: (q.scoreBands || []).map((band) =>
          band.id === bandId ? { ...band, [field]: value } : band
        ),
      };
      return copy;
    });
  };

  const removeScoreBand = (qIndex, bandId) => {
    setQuestions((prev) => {
      const copy = [...prev];
      const q = copy[qIndex];
      copy[qIndex] = { ...q, scoreBands: (q.scoreBands || []).filter((band) => band.id !== bandId) };
      return copy;
    });
  };
  // --------------------------------------------------------------------

  const buildPayload = (question) => {
    if (question.scoreShape === "scale") {
      return {
        questionUID: question.questionUID,
        scoreBands: (question.scoreBands || []).map((band) => ({
          label: band.label,
          min: Number(band.min) || 0,
          max: Number(band.max) || 0,
          score: Number(band.score) || 0,
        })),
      };
    }

    const isSectionA = question.section === "Section A";
    const primaryFactorId = question.factor?.[0];

    return {
      questionUID: question.questionUID,
      questionFactors: isSectionA ? question.factor : [],
      options: question.options.map((option) => ({
        optionID: option.optionID,
        score: isSectionA ? Number(option.scores?.[primaryFactorId] ?? 0) : Number(option.score ?? 0),
        isGateKeeper: isSectionA ? !!option.isGatekeeper : false,
        isDevelopment: !isSectionA && option.classification === "Developmental",
        isRedFlag: !isSectionA && option.classification === "RedFlag",
      })),
    };
  };

  const handleBuildAnalytics = async () => {
    try {
      const question = questions[selectedQuestion];
      if (!question) return;

      if (question.scoreShape === "options" && question.section === "Section A" && question.factor.length === 0) {
        CustomToast("fail", "Please select at least one Assessment Factor.");
        return;
      }

      if (question.scoreShape === "scale" && (question.scoreBands || []).length === 0) {
        CustomToast("fail", "Add at least one score band before saving.");
        return;
      }

      const payload = buildPayload(question);
      const response = await saveQuestionAnalytics(payload);

      if (response.data.status.toLowerCase() === "success") {
        CustomToast("success", "Question analytics saved successfully.");
      } else {
        CustomToast("fail", response.data.message);
      }
    } catch (error) {
      CustomToast("fail", "Something went wrong.");
    }
  };

  const loadQuestionAnalytics = async (questionUID, questionIndex) => {
    try {
      const response = await getQuestionAnalytics(questionUID);
      if (response.data.status?.toLowerCase() !== "success") {
        CustomToast("fail", response.data.message || "Failed to fetch question analysis.");
        return;
      }

      const analytics = response.data;
      const copy = [...questions];
      const question = copy[questionIndex];

      if (question.scoreShape === "scale") {
        const savedBands = analytics.scoreBands || [];
        if (savedBands.length > 0) {
          question.isAnalyticsEnabled = true;
          question.scoreBands = savedBands.map((band) => ({
            id: makeId(),
            label: band.label ?? "Band",
            min: band.min ?? 0,
            max: band.max ?? 0,
            score: band.score ?? 0,
          }));
          setQuestions(copy);
        }
        return;
      }

      const factors = analytics.questionFactors || [];
      const savedOptions = analytics.options || [];

      const hasSavedAnalysis =
        factors.length > 0 ||
        savedOptions.some(
          (o) => (o.score !== null && o.score !== undefined) || o.isGateKeeper || o.isDevelopment || o.isRedFlag,
        );
      if (!hasSavedAnalysis) return;

      question.isAnalyticsEnabled = true;

      if (question.section === "Section A") {
        question.factor = factors;
        question.options = question.options.map((option) => {
          const saved = savedOptions.find((x) => x.optionID === option.optionID);
          const score = saved?.score ?? 0;
          const scores = {};
          factors.forEach((factorId) => { scores[factorId] = score; });
          return { ...option, scores, isGatekeeper: !!saved?.isGateKeeper };
        });
      } else {
        question.options = question.options.map((option) => {
          const saved = savedOptions.find((x) => x.optionID === option.optionID);
          let classification = null;
          if (saved?.isDevelopment) classification = "Developmental";
          else if (saved?.isRedFlag) classification = "RedFlag";
          return { ...option, score: saved?.score ?? 0, classification };
        });
      }

      setQuestions(copy);
    } catch (err) {
      CustomToast("fail", "Couldn't fetch question analysis.");
    }
  };

  const selectQuestion = async (question, globalIndex, section) => {
    setSelectedSection(section);
    setSelectedQuestion(globalIndex);
    if (question.scoreShape !== "none") {
      await loadQuestionAnalytics(question.questionUID, globalIndex);
    }
  };

  const handleAnalyticsToggle = async (question, globalIndex, section, checked) => {
    if (question.scoreShape === "none") return;
    setSelectedSection(section);
    setSelectedQuestion(globalIndex);
    if (checked) {
      await loadQuestionAnalytics(question.questionUID, globalIndex);
      setQuestions((prev) => {
        const copy = [...prev];
        copy[globalIndex] = { ...copy[globalIndex], isAnalyticsEnabled: true };
        return copy;
      });
      return;
    }

    setQuestions((prev) => {
      const copy = [...prev];
      const q = { ...copy[globalIndex], isAnalyticsEnabled: false };

      if (q.scoreShape === "scale") {
        q.scoreBands = [];
      } else if (q.section === "Section A") {
        q.factor = [];
        q.options = q.options.map((o) => ({ ...o, scores: {}, isGatekeeper: false }));
      } else {
        q.options = q.options.map((o) => ({ ...o, score: 0, classification: null }));
      }

      copy[globalIndex] = q;
      return copy;
    });
  };

  const toggleSection = (section) => {
    setSelectedSection(section);
    setExpandedSection((current) => (current === section ? null : section));
  };

  // Generic Band Handlers
  const updateFactor1Band = (bandId, field, value) => {
    setFactor1Bands((prev) => prev.map((band) => (band.id === bandId ? { ...band, [field]: value } : band)));
  };

  const updateFactor2Band = (bandId, field, value) => {
    setFactor2Bands((prev) => prev.map((band) => (band.id === bandId ? { ...band, [field]: value } : band)));
  };

  const updateGridCell = (factor1Id, factor2Id, field, value) => {
    setGrid((prev) => ({
      ...prev,
      [factor1Id]: {
        ...prev[factor1Id],
        [factor2Id]: { ...prev[factor1Id]?.[factor2Id], [field]: value },
      },
    }));
  };

  const updateSectionBBand = (bandId, field, value) => { setSectionBBands((prev) => prev.map((band) => (band.id === bandId ? { ...band, [field]: value } : band))); };

  const addSectionBBand = () => { setSectionBBands((prev) => [...prev, { id: makeId(), label: "New band", min: 0, max: 0 }]); };

  const removeSectionBBand = (bandId) => { setSectionBBands((prev) => prev.filter((band) => band.id !== bandId)); };

  const sectionCounts = DEFAULT_SECTIONS.reduce((counts, section) => {
    counts[section] = questions.filter((q) => q.section === section).length; return counts;
  }, {});
  const activeQuestion = questions[selectedQuestion];

  return (
    <>
      <div className="flex h-screen flex-col overflow-hidden bg-[#f7f8fa]">
        <div className="flex-shrink-0">
          <div className="mx-auto max-w-[1440px] pt-2">
            <SurveyNavTabs
              active="analytics"
              surveyUID={surveyUID}
              surveyName={survey?.surveyName}
              surveyDescription={survey?.surveyDescription}
              viewOnly={viewOnly}
              unlockAnalytics={unlockAnalytics}
              surveyStatus={location.state?.surveyStatus || survey?.surveyStatus}
            />
          </div>

          <div className="mx-auto flex max-w-[1440px] items-center justify-between py-4">
            <div className="flex items-center gap-1 rounded-xl bg-gray-100 p-1">
              <button
                type="button"
                onClick={() => setViewMode("questions")}
                className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition ${viewMode === "questions" ? "bg-white text-[#7f35b2] shadow-sm" : "text-gray-500 hover:text-gray-700"
                  }`}
              >
                <ListChecks size={15} />
                Question Scoring
              </button>
              <button
                type="button"
                onClick={() => setViewMode("rules")}
                className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition ${viewMode === "rules" ? "bg-white text-[#7f35b2] shadow-sm" : "text-gray-500 hover:text-gray-700"
                  }`}
              >
                <SlidersHorizontal size={15} />
                Decision Rules
              </button>
            </div>

            {/* {!viewOnly && (
              <button
                type="button"
                onClick={() => setShowFactorDrawer(true)}
                className="flex items-center gap-2 rounded-full bg-[#7f35b2] px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-[#3bb290]"
              >
                <ClipboardList size={16} />
                Scoring Type
              </button>
            )} */} 

            {!analyticsViewOnly && (
              <button
                type="button"
                onClick={() => setShowFactorDrawer(true)}
                className="flex items-center gap-2 rounded-full bg-[#7f35b2] px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-[#3bb290]"
              >
                <ClipboardList size={16} />
                Scoring Type
              </button>
            )}
          </div>
        </div>

        {viewMode === "rules" ? (
          <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-6 shadow-xl bg-white rounded-xl">
            <div className="mx-auto max-w-[1440px] space-y-2">
              <div className="space-y-4">
                <div className="px-1">
                  <h2 className="text-lg font-semibold text-gray-900">Section A — Assessment Decision Grid</h2>
                  <p className="mt-1 text-sm text-gray-500">
                    Select the factors for the decision grid. Rows represent the Y-Axis bands, columns represent the X-Axis bands. Cells where either axis is "Below Average" are automatically treated as "Do not proceed".
                  </p>
                </div>

                {factor1ID && factor2ID && factor1ID === factor2ID && (
                  <div className="mt-3 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                    <ShieldAlert size={13} className="flex-shrink-0" />
                    Y-Axis and X-Axis are using the same factor. Loaded from an older config — pick a different factor for one axis before saving.
                  </div>
                )}

                <div className="flex flex-col xl:flex-row gap-4 items-start overflow-x-auto pb-2">
                  {/* Left Panel - Y Axis */}
                  <div className="w-full xl:w-[260px] my-auto flex items-center justify-center flex-col shrink-0 rounded-2xl bg-[#c2a8f01a] p-4 border border-[#c2a8f033] shadow-md">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="p-1.5 bg-[#c2a8f033] rounded-md text-[#7f35b2]">
                        <BarChart3 size={16} />
                      </div>
                      <span className="text-sm font-semibold text-gray-800">Y Axis Factor (Rows)</span>
                    </div>
                    <Select
                      isDisabled={analyticsViewOnly}
                      options={assessmentFactor
                        .filter((item) => item.assessmentFactorID !== factor2ID)
                        .map((item) => ({ value: item.assessmentFactorID, label: item.assessmentFactor }))}
                      value={
                        assessmentFactor.find((item) => item.assessmentFactorID === factor1ID)
                          ? { value: factor1ID, label: assessmentFactor.find((item) => item.assessmentFactorID === factor1ID).assessmentFactor }
                          : null
                      }
                      onChange={(selected) => setFactor1ID(selected ? selected.value : null)}
                      placeholder="Assessment Factor"
                      styles={singleSelectStyles}
                    />
                  </div>

                  {/* Right Panel - X Axis & Grid */}
                  <div className="flex-1 min-w-0 flex flex-col gap-4">
                    {/* X Axis Header */}
                    <div className="flex items-center justify-center gap-3 rounded-2xl bg-[#c2a8f01a] p-2.5 border border-[#c2a8f033] shadow-md">
                      <div className="flex items-center gap-2 pl-2">
                        <div className="p-1.5 bg-[#c2a8f033] rounded-md text-[#7f35b2]">
                          <LayoutGrid size={16} />
                        </div>
                        <span className="text-sm font-semibold text-gray-800 whitespace-nowrap">X Axis Factor (Columns)</span>
                      </div>
                      <div className="flex-1 max-w-sm">
                        <Select
                          isDisabled={analyticsViewOnly}
                          options={assessmentFactor
                            .filter((item) => item.assessmentFactorID !== factor1ID)
                            .map((item) => ({ value: item.assessmentFactorID, label: item.assessmentFactor }))}
                          value={
                            assessmentFactor.find((item) => item.assessmentFactorID === factor2ID)
                              ? { value: factor2ID, label: assessmentFactor.find((item) => item.assessmentFactorID === factor2ID).assessmentFactor }
                              : null
                          }
                          onChange={(selected) => setFactor2ID(selected ? selected.value : null)}
                          placeholder="Select X Axis Factor"
                          styles={singleSelectStyles}
                        />
                      </div>
                    </div>

                    {/* Grid Area */}
                    <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white shadow-lg">
                      <table className="w-full border-separate border-spacing-3">
                        <thead>
                          <tr>
                            <th className="align-top text-left min-w-[180px] pb-2">
                              <div className="flex items-center gap-1.5 text-sm font-bold text-gray-800 pt-1 pl-1">
                                Assessment Factor <Info size={14} className="text-gray-400" />
                              </div>
                            </th>
                            {factor2Bands.map((f2Band) => (
                              <th key={f2Band.id} className="min-w-[220px] text-left pb-2">
                                <input
                                  value={f2Band.label}
                                  disabled={analyticsViewOnly}
                                  onChange={(e) => updateFactor2Band(f2Band.id, "label", e.target.value)}
                                  className="w-full px-2 py-1.5 text-sm font-bold text-gray-800 bg-white border border-gray-200 rounded-md shadow-sm focus:border-[#7f35b2] focus:ring-1 focus:ring-[#7f35b2]/20 focus:outline-none mb-2 transition-all disabled:bg-gray-50 disabled:text-gray-400"
                                />
                                <GridBandRangeInputs
                                  band={f2Band}
                                  disabled={analyticsViewOnly}
                                  onChange={(field, value) => updateFactor2Band(f2Band.id, field, value)}
                                />
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {factor1Bands.map((f1Band) => (
                            <tr key={f1Band.id}>
                              <td className="align-top pt-4 pb-3 pr-4 pl-1">
                                <input
                                  value={f1Band.label}
                                  disabled={analyticsViewOnly}
                                  onChange={(e) => updateFactor1Band(f1Band.id, "label", e.target.value)}
                                  className="w-full px-2 py-1.5 text-sm font-bold text-gray-800 bg-white border border-gray-200 rounded-md shadow-sm focus:border-[#7f35b2] focus:ring-1 focus:ring-[#7f35b2]/20 focus:outline-none mb-2 transition-all disabled:bg-gray-50 disabled:text-gray-400"
                                />
                                <GridBandRangeInputs
                                  band={f1Band}
                                  disabled={analyticsViewOnly}
                                  onChange={(field, value) => updateFactor1Band(f1Band.id, field, value)}
                                />
                              </td>

                              {factor2Bands.map((f2Band) => {
                                const cell = grid[f1Band.id]?.[f2Band.id] || { grade: "", title: "", description: "" };
                                const mustNotProceed = f1Band.id === "below" || f2Band.id === "below";

                                // Dynamic styles matching the image
                                let cellStyle = "border-gray-200";
                                let badgeStyle = "bg-gray-100 text-gray-700";

                                if (mustNotProceed) {
                                  cellStyle = "border-red-200";
                                  badgeStyle = "bg-red-100 text-red-600";
                                } else if (f2Band.id === "above") {
                                  cellStyle = "border-green-200";
                                  badgeStyle = "bg-green-100 text-green-700";
                                }

                                return (
                                  <td
                                    key={f2Band.id}
                                    className={`align-top rounded-xl border p-4 transition-colors ${cellStyle}`}
                                  >
                                    <div className="mb-2">
                                      <input
                                        value={cell.grade}
                                        disabled={analyticsViewOnly}
                                        onChange={(e) => updateGridCell(f1Band.id, f2Band.id, "grade", e.target.value)}
                                        placeholder="Grade"
                                        className={`w-10 h-8 rounded-md border border-transparent focus:border-[#7f35b2] focus:ring-1 focus:ring-[#7f35b2]/20 focus:bg-white focus:outline-none text-center text-xs font-bold transition-all disabled:opacity-70 ${badgeStyle}`}
                                      />
                                    </div>
                                    <input
                                      value={cell.title}
                                      disabled={analyticsViewOnly}
                                      onChange={(e) => updateGridCell(f1Band.id, f2Band.id, "title", e.target.value)}
                                      placeholder="Title"
                                      className="w-full px-2 py-1.5 text-sm font-bold text-gray-800 bg-white border border-gray-200 rounded-md shadow-sm focus:border-[#7f35b2] focus:ring-1 focus:ring-[#7f35b2]/20 focus:outline-none mb-2 transition-all disabled:bg-gray-50 disabled:text-gray-400"
                                    />
                                    <textarea
                                      value={cell.description}
                                      disabled={analyticsViewOnly}
                                      onChange={(e) => updateGridCell(f1Band.id, f2Band.id, "description", e.target.value)}
                                      placeholder="Description"
                                      rows={3}
                                      className="w-full px-2 py-1.5 resize-none text-xs text-gray-700 bg-white border border-gray-200 rounded-md shadow-sm focus:border-[#7f35b2] focus:ring-1 focus:ring-[#7f35b2]/20 focus:outline-none leading-snug transition-all disabled:bg-gray-50 disabled:text-gray-400"
                                    />
                                    {mustNotProceed && (
                                      <div className="mt-2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-red-500">
                                        <Ban size={12} strokeWidth={2.5} />
                                        DO NOT PROCEED (AUTOMATIC)
                                      </div>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-gray-900">Section B — Score Bands & Red Flags</h2>
                <p className="mt-1 text-sm text-gray-500">
                  Final recommendation bands based on total accumulated marks in Section B.
                </p>

                <div className="mt-4 space-y-2">
                  {sectionBBands.map((band) => (
                    <div key={band.id} className="flex items-center gap-2 rounded-lg border border-gray-100 bg-gray-50/60 px-3 py-2">
                       <input
                        value={band.label}
                        disabled={analyticsViewOnly}
                        onChange={(e) => updateSectionBBand(band.id, "label", e.target.value)}
                        className="flex-1 rounded-md border border-gray-200 bg-white px-2 py-1.5 text-sm focus:border-[#7f35b2]"
                      />
                      <BandRangeInputs
                        band={band}
                        disabled={analyticsViewOnly}
                        onChange={(field, value) => updateSectionBBand(band.id, field, value)}
                      />
                      {!analyticsViewOnly && (
                        <button
                          type="button"
                          onClick={() => removeSectionBBand(band.id)}
                          className="rounded-md p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-500"
                          title="Remove band"
                        >
                          <Trash2 size={15} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                {!analyticsViewOnly && (
                  <button
                    type="button"
                    onClick={addSectionBBand}
                    className="mt-3 flex items-center gap-1.5 text-sm font-medium text-[#7f35b2] hover:text-[#48086f]"
                  >
                    <Plus size={14} />
                    Add band
                  </button>
                )}
              </div>

              {!analyticsViewOnly && (
                <div className="fixed bottom-6 right-6 z-50 flex justify-end">
                  <button
                    type="button"
                    onClick={handleSaveScoringConfig}
                    disabled={savingConfig || !factor1ID || !factor2ID || factor1ID === factor2ID}
                    className="rounded-xl bg-[#7f35b2] px-5 py-2.5 text-sm font-medium text-white shadow-lg transition hover:bg-[#3bb290] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {savingConfig ? "Saving..." : "Save Scoring Rules"}
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="min-h-0 flex-1 overflow-hidden pb-6">
            <div className="flex h-full max-w-[1390px] gap-6">
              {/* LEFT PANEL */}
              <div className="flex h-full w-[320px] flex-shrink-0 flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
                <div className="flex-shrink-0 p-5 pb-0">
                  {survey && (
                    <div className="rounded-xl bg-gradient-to-br from-[#7f35b2]/10 to-[#7f35b2]/5 p-4">
                      <div className="mb-1 flex items-start justify-between gap-2">
                        <h2 className="text-base font-semibold leading-tight text-gray-900">{survey.surveyName}</h2>
                        <span className="flex-shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-700">
                          {survey.surveyStatus === "Incomplete" ? "Draft" : survey.surveyStatus}
                        </span>
                      </div>
                      <style>{DESCRIPTION_RICH_CONTENT_STYLES}</style>
                      <div
                        className="description-rich-content text-xs text-gray-600 line-clamp-5"
                        title={stripHtml(survey.surveyDescription)}
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(survey.surveyDescription) }}
                      />
                    </div>
                  )}
                </div>

                <div className="flex-1 overflow-y-auto p-5 pt-4">
                  <div className="mb-2 px-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">Sections</div>

                  <div className="space-y-2">
                    {DEFAULT_SECTIONS.map((section) => {
                      const isSelected = selectedSection === section;
                      const isExpanded = expandedSection === section;
                      const sectionList = questions.filter((q) => q.section === section);

                      return (
                        <div
                          key={section}
                          className={`rounded-xl border transition-colors ${isSelected ? "border-[#7f35b2]/30 bg-[#7f35b2]/5" : "border-transparent bg-gray-50"
                            }`}
                        >
                          <button
                            type="button"
                            onClick={() => toggleSection(section)}
                            aria-expanded={isExpanded}
                            className="flex w-full items-center justify-between px-3 py-2.5"
                          >
                            <div className="flex items-center gap-2">
                              <Layers size={14} className={isSelected ? "text-[#7f35b2]" : "text-gray-400"} />
                              <span className={`text-sm font-medium ${isSelected ? "text-[#7f35b2]" : "text-gray-700"}`}>
                                {section}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span
                                className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${isSelected ? "bg-[#7f35b2] text-white" : "bg-gray-200 text-gray-600"
                                  }`}
                              >
                                {sectionCounts[section] || 0}
                              </span>
                              <motion.span animate={{ rotate: isExpanded ? 180 : 0 }} transition={{ duration: 0.2 }} className="text-gray-400">
                                <ChevronDown size={14} />
                              </motion.span>
                            </div>
                          </button>

                          <AnimatePresence initial={false}>
                            {isExpanded && (
                              <motion.div
                                key="content"
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
                                className="max-h-[200px] overflow-y-auto"
                              >
                                <div className="space-y-1 px-2 pb-2">
                                  {sectionList.map((question) => {
                                    const globalIndex = questions.findIndex((q) => q.questionUID === question.questionUID);
                                    const hasGatekeeper =
                                      question.section === "Section A" && question.options.some((o) => o.isGatekeeper);
                                    const hasRedFlag =
                                      question.section === "Section B" &&
                                      question.options.some((o) => o.classification === "RedFlag");
                                    const isNotScoreable = question.scoreShape === "none";

                                    return (
                                      <div
                                        key={question.questionUID}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => selectQuestion(question, globalIndex, section)}
                                        className={`group flex items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors ${isNotScoreable ? "cursor-pointer opacity-60" : "cursor-pointer"
                                          } ${selectedQuestion === globalIndex ? "bg-white shadow-sm" : "hover:bg-white"}`}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={question.isAnalyticsEnabled}
                                          disabled={isNotScoreable}
                                          title={isNotScoreable ? `${question.optionType} questions can't be scored` : undefined}
                                          onChange={(e) => handleAnalyticsToggle(question, globalIndex, section, e.target.checked)}
                                          onClick={(e) => e.stopPropagation()}
                                          aria-label="Enable analytics for this question"
                                          className="h-4 w-4 flex-shrink-0 cursor-pointer appearance-none rounded-full border-2 border-gray-300 bg-white transition-colors checked:border-[#7f35b2] checked:bg-[#7f35b2] checked:ring-2 checked:ring-[#7f35b2]/20 disabled:cursor-not-allowed disabled:opacity-40"
                                        />
                                        <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md bg-gray-100 text-[10px] font-semibold text-gray-500">
                                          {sectionList.indexOf(question) + 1}
                                        </span>
                                        <div className="min-w-0 flex-1">
                                          <div className="flex items-center justify-between gap-1">
                                            <span className="line-clamp-1 text-xs text-gray-600">
                                              {stripHtml(question.questionName) || "Untitled Question"}
                                            </span>
                                            <div className="flex flex-shrink-0 items-center gap-1">
                                              {hasGatekeeper && (
                                                <ShieldAlert size={11} className="text-amber-500" title="Has a gatekeeper (screen-out) option" />
                                              )}
                                              {hasRedFlag && <Flag size={11} className="text-red-500" title="Has a red-flag option" />}
                                              {isNotScoreable && (
                                                <span
                                                  className="flex flex-shrink-0 items-center gap-0.5 rounded-full bg-gray-200 px-1.5 py-0.5 text-[9px] text-gray-500"
                                                  title="This question type can't be scored"
                                                >
                                                  <Ban size={9} />
                                                  Not scoreable
                                                </span>
                                              )}
                                              {question.isAnalyticsEnabled && (
                                                <span className="flex-shrink-0 rounded-full bg-green-100 px-1.5 py-0.5 text-[9px] text-green-700">
                                                  Analytics
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                          <div className="mt-0.5 flex flex-wrap items-center gap-1 text-[10px] text-gray-400">
                                            <span>{question.channel}</span>
                                            {question.experienceType && (
                                              <>
                                                <span>·</span>
                                                <span>{question.experienceType}</span>
                                              </>
                                            )}
                                            {question.optionType && (
                                              <>
                                                <span>·</span>
                                                <span className="inline-block rounded bg-gray-100 px-1 py-0.5 text-[9px] font-medium text-gray-500">
                                                  {question.optionType}
                                                </span>
                                              </>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* RIGHT PANEL */}
              <div className="flex h-full min-w-0 flex-1 flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
                {questions.length > 0 && activeQuestion ? (
                  <>
                    <div className="sticky top-0 z-10 flex items-start justify-between border-b border-gray-100 bg-white px-3 py-4">
                      <div className="flex items-center gap-4">
                        <div
                          className={`h-10 w-10 rounded-full flex items-center justify-center font-bold text-white ${activeQuestion.isAnalyticsEnabled ? "bg-[#7f35b2]" : "bg-gray-400"
                            }`}
                        >
                          {selectedQuestion + 1}
                        </div>
                        <div>
                          <h2 className="text-lg font-semibold">Question {selectedQuestion + 1}</h2>
                          <p className="text-sm text-gray-500">{stripHtml(activeQuestion.questionName)}</p>
                          <div className="mt-2 flex items-center gap-2 flex-wrap">
                            {activeQuestion.scoreShape === "none" ? (
                              <span className="flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">
                                <Ban size={11} />
                                Not scoreable
                              </span>
                            ) : activeQuestion.isAnalyticsEnabled ? (
                              <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">Analytics Enabled</span>
                            ) : (
                              <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">Analytics Disabled</span>
                            )}
                            <span className="px-2 py-1 text-xs rounded-full bg-blue-50 text-blue-600">{activeQuestion.section}</span>
                            <span className="px-2 py-1 text-xs rounded-full bg-slate-100 text-slate-600">{activeQuestion.optionType}</span>
                            {activeQuestion.channel && (
                              <span className="px-2 py-1 text-xs rounded-full bg-purple-50 text-purple-600">{activeQuestion.channel}</span>
                            )}
                            {activeQuestion.experienceType && (
                              <span className="px-2 py-1 text-xs rounded-full bg-amber-50 text-amber-600">{activeQuestion.experienceType}</span>
                            )}
                          </div>
                        </div>
                      </div>

                      {activeQuestion.isAnalyticsEnabled && activeQuestion.scoreShape === "options" && activeQuestion.section === "Section A" && (
                        <div className="w-72">
                          <label className="text-xs font-medium text-gray-500 block mb-2">Assessment Factors</label>
                          <Select
                            isMulti
                            isDisabled={analyticsViewOnly}
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            controlShouldRenderValue={false}
                            placeholder={
                              activeQuestion.factor.length === 0 ? "Select Assessment Factors" : `${activeQuestion.factor.length} Factor(s) Selected`
                            }
                            options={assessmentFactor.map((item) => ({ value: item.assessmentFactorID, label: item.assessmentFactor }))}
                            value={assessmentFactor
                              .filter((item) => activeQuestion.factor.includes(item.assessmentFactorID))
                              .map((item) => ({ value: item.assessmentFactorID, label: item.assessmentFactor }))}
                            onChange={(selected) => updateFactorSelection(selectedQuestion, selected ? selected.map((x) => x.value) : [])}
                            classNamePrefix="assessment"
                            styles={{
                              control: (provided, state) => ({
                                ...provided,
                                minHeight: 40,
                                borderRadius: 8,
                                borderColor: state.isFocused ? "#7f35b2" : "#d1d5db",
                                boxShadow: state.isFocused ? "0 0 0 2px rgba(59,178,144,.20)" : "none",
                                "&:hover": { borderColor: "#7f35b2" },
                              }),
                              menu: (provided) => ({ ...provided, zIndex: 9999 }),
                              menuList: (provided) => ({ ...provided, padding: 0 }),
                              option: (provided, state) => ({
                                ...provided,
                                backgroundColor: state.isSelected ? "#7f35b2" : state.isFocused ? "#c2a8f026" : "#fff",
                                color: state.isSelected ? "#fff" : "#111827",
                                cursor: "pointer",
                                ":active": { backgroundColor: "#c2a8f066" },
                              }),
                              multiValue: () => ({ display: "none" }),
                              placeholder: (provided) => ({ ...provided, color: "#6B7280", fontWeight: 500 }),
                              dropdownIndicator: (provided, state) => ({
                                ...provided,
                                color: state.isFocused ? "#7f35b2" : "#6B7280",
                                ":hover": { color: "#7f35b2" },
                              }),
                              clearIndicator: (provided) => ({ ...provided, color: "#7f35b2", ":hover": { color: "#48086f" } }),
                              indicatorSeparator: () => ({ display: "none" }),
                              input: (provided) => ({ ...provided, color: "#111827" }),
                              singleValue: (provided) => ({ ...provided, color: "#111827" }),
                            }}
                            formatOptionLabel={(option, { context }) => {
                              if (context !== "menu") return option.label;
                              const checked = activeQuestion.factor.includes(option.value);
                              return (
                                <div className="flex items-center justify-between w-full">
                                  <div className="flex items-center gap-3">
                                    <input type="checkbox" checked={checked} readOnly className="h-4 w-4 accent-[#7f35b2]" />
                                    <span>{option.label}</span>
                                  </div>
                                </div>
                              );
                            }}
                          />
                        </div>
                      )}
                    </div>

                    {/* Body */}
                    {activeQuestion.scoreShape === "none" ? (
                      <div className="flex-1 flex items-center justify-center bg-gray-50">
                        <div className="text-center max-w-md">
                          <div className="w-20 h-20 mx-auto rounded-full bg-gray-200 flex items-center justify-center">
                            <Ban className="text-gray-500" size={32} />
                          </div>
                          <h3 className="mt-5 text-lg font-semibold text-gray-700">Not scoreable</h3>
                          <p className="mt-2 text-sm text-gray-500">
                            "{activeQuestion.optionType}" questions collect free-text answers and don't have a
                            fixed answer set, so they can't be scored here.
                          </p>
                        </div>
                      </div>
                    ) : !activeQuestion.isAnalyticsEnabled ? (
                      <div className="flex-1 flex items-center justify-center bg-gray-50">
                        <div className="text-center max-w-md">
                          <div className="w-20 h-20 mx-auto rounded-full bg-gray-200 flex items-center justify-center">
                            <BarChart3 className="text-gray-500" size={38} />
                          </div>
                          <h3 className="mt-5 text-lg font-semibold text-gray-700">Analytics Disabled</h3>
                          <p className="mt-2 text-sm text-gray-500">
                            Enable the checkbox for this question from the left panel to configure scoring.
                          </p>
                        </div>
                      </div>
                    ) : activeQuestion.scoreShape === "scale" ? (
                      <div className="flex-1 overflow-y-auto p-6 space-y-3 bg-gray-50">
                        <div className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                          <Info size={16} className="flex-shrink-0" />
                          Define score ranges for this {activeQuestion.optionType.toLowerCase()} question — e.g.
                          "0–2 → 1pt", "3–5 → 3pts", "6+ → 5pts". Ranges shouldn't overlap.
                        </div>

                        <div className="space-y-2">
                          {(activeQuestion.scoreBands || []).map((band) => (
                            <div key={band.id} className="flex items-center gap-2 rounded-xl border bg-white px-4 py-3">
                              <input
                                value={band.label}
                                disabled={analyticsViewOnly}
                                onChange={(e) => updateScoreBand(selectedQuestion, band.id, "label", e.target.value)}
                                placeholder="Band label"
                                className="flex-1 rounded-md border border-gray-200 px-2 py-1.5 text-sm focus:border-[#7f35b2]"
                              />
                              <BandRangeInputs
                                band={band}
                                disabled={analyticsViewOnly}
                                onChange={(field, value) => updateScoreBand(selectedQuestion, band.id, field, value)}
                              />
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs text-gray-500">Score</span>
                                <input
                                  type="number"
                                  disabled={analyticsViewOnly}
                                  value={band.score}
                                  onChange={(e) => updateScoreBand(selectedQuestion, band.id, "score", e.target.value)}
                                  className="w-16 rounded-md border text-center py-1.5 text-sm focus:border-[#7f35b2] focus:ring-2 focus:ring-[#7f35b2]/20"
                                />
                              </div>
                              {!analyticsViewOnly && (
                                <button
                                  type="button"
                                  onClick={() => removeScoreBand(selectedQuestion, band.id)}
                                  className="rounded-md p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-500"
                                  title="Remove band"
                                >
                                  <Trash2 size={15} />
                                </button>
                              )}
                            </div>
                          ))}

                          {(activeQuestion.scoreBands || []).length === 0 && (
                            <div className="rounded-xl border border-dashed border-gray-300 bg-white p-6 text-center text-sm text-gray-500">
                              No score bands yet. Add one to start scoring this question.
                            </div>
                          )}
                        </div>

                        <div className="flex items-center gap-3 pt-1">
                          {!analyticsViewOnly && (
                            <button
                              type="button"
                              onClick={() => addScoreBand(selectedQuestion)}
                              className="flex items-center gap-1.5 text-sm font-medium text-[#7f35b2] hover:text-[#48086f]"
                            >
                              <Plus size={14} />
                              Add band
                            </button>
                          )}
                        </div>

                        {!analyticsViewOnly && (
                          <button
                            onClick={handleBuildAnalytics}
                            className="bg-[#7f35b2] hover:bg-[#3bb290] text-white px-5 py-2 rounded-xl text-sm font-medium transition"
                          >
                            Build Analytics
                          </button>
                        )}
                      </div>
                    ) : activeQuestion.section === "Section A" ? (
                      <div className="flex-1 overflow-y-auto p-6 space-y-3 bg-gray-50">
                        {activeQuestion.factor.length === 0 && (
                          <div className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                            <Info size={16} className="flex-shrink-0" />
                            Select at least one Assessment Factor above to start scoring options.
                          </div>
                        )}

                        {activeQuestion.options.map((option, optionIndex) => (
                          <div key={optionIndex} className="rounded-xl border bg-white px-4 py-3 hover:border-[#7f35b2]">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                              <div className="flex items-center gap-4 text-sm w-[50%]">
                                {activeQuestion.optionType === "Multiple Choice" || activeQuestion.optionType === "Dropdown" ? (
                                  <input type="radio" disabled className="accent-[#7f35b2]" />
                                ) : (
                                  <input type="checkbox" disabled className="accent-[#7f35b2]" />
                                )}
                                <span className="font-medium">{option.optionName}</span>
                              </div>

                              <div className="flex items-center gap-4">
                                {activeQuestion.factor.length > 0 && (
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs text-gray-500">Score</span>
                                     <input
                                      type="number"
                                      min={0}
                                      disabled={analyticsViewOnly}
                                      value={option.scores?.[activeQuestion.factor[0]] ?? 0}
                                      onChange={(e) => updateOptionScoreForAllFactors(selectedQuestion, optionIndex, e.target.value)}
                                      className="w-16 rounded-md border text-center py-1.5 text-sm focus:border-[#7f35b2] focus:ring-2 focus:ring-[#7f35b2]/20"
                                    />
                                  </div>
                                )}

                                <label
                                  className="flex items-center gap-1.5 text-xs font-medium"
                                  title="Selecting this option screens the candidate out regardless of total score (internal use only)"
                                >
                                  <input
                                    type="checkbox"
                                    disabled={analyticsViewOnly}
                                    checked={!!option.isGatekeeper}
                                    onChange={(e) => updateGatekeeper(selectedQuestion, optionIndex, e.target.checked)}
                                    className="h-4 w-4 accent-red-500"
                                  />
                                  <ShieldAlert size={13} className={option.isGatekeeper ? "text-red-500" : "text-gray-300"} />
                                  Gatekeeper
                                </label>
                              </div>
                            </div>
                          </div>
                        ))}
                        {!analyticsViewOnly  && (
                          <button
                            onClick={handleBuildAnalytics}
                            className="bg-[#7f35b2] hover:bg-[#3bb290] text-white px-5 py-2 rounded-xl text-sm font-medium transition"
                          >
                            Build Analytics
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="flex-1 overflow-y-auto p-6 space-y-3 bg-gray-50">
                        {activeQuestion.options.map((option, optionIndex) => (
                          <div key={optionIndex} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-white px-4 py-3 hover:border-[#7f35b2]">
                            <div className="flex items-center gap-4 text-sm w-[50%]">
                              {activeQuestion.optionType === "Multiple Choice" || activeQuestion.optionType === "Dropdown" ? (
                                <input type="radio" disabled className="accent-[#7f35b2]" />
                              ) : (
                                <input type="checkbox" disabled className="accent-[#7f35b2]" />
                              )}
                              <span className="font-medium">{option.optionName}</span>
                            </div>

                            <div className="flex items-center gap-4">
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500">Score</span>
                                <input
                                  type="number"
                                  min={0}
                                  disabled={analyticsViewOnly}
                                  value={option.score ?? 0}
                                  onChange={(e) => updateOptionScoreB(selectedQuestion, optionIndex, e.target.value)}
                                  className="w-16 rounded-md border text-center py-1.5 text-sm focus:border-[#7f35b2] focus:ring-2 focus:ring-[#7f35b2]/20"
                                />
                              </div>

                              <ClassificationPicker
                                value={option.classification}
                                disabled={analyticsViewOnly}
                                onChange={(classification) => updateClassification(selectedQuestion, optionIndex, classification)}
                              />
                            </div>
                          </div>
                        ))}

                        {!analyticsViewOnly  && (
                          <button
                            onClick={handleBuildAnalytics}
                            className="bg-[#7f35b2] hover:bg-[#3bb290] text-white px-5 py-2 rounded-xl text-sm font-medium transition"
                          >
                            Build Analytics
                          </button>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex flex-1 flex-col items-center justify-center gap-3 bg-gray-50 text-center">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
                      <BarChart3 className="text-gray-400" size={30} />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700">No questions yet</h3>
                      <p className="mt-1 max-w-xs text-xs text-gray-500">
                        Add questions in the survey builder — they'll show up here.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <AddAssessmentFactor
        open={showFactorDrawer}
        onClose={() => setShowFactorDrawer(false)}
        surveyUID={surveyUID}
        onSuccess={() => { loadAssessmentFactors(); }}
      />
    </>
  );
};

export default BuildAnalytics;

